'use client';

import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { useGlobalStore } from '@/store/global';
import { globalGeneralSelectors } from '@/store/global/selectors';
import { getMessages } from '@/i18n/messages';
import { defaultLocale, locales, type Locale } from '@/i18n';

interface DynamicLocaleProviderProps extends PropsWithChildren {
  initialLocale: Locale;
  initialMessages: AbstractIntlMessages;
}

function resolveBrowserLocale(): Locale {
  const browserLocale = (navigator.language || '').toLowerCase();

  const exactMatch = locales.find(locale => locale.toLowerCase() === browserLocale);
  if (exactMatch) return exactMatch;

  const languageCode = browserLocale.split('-')[0] as Locale;
  if (locales.includes(languageCode)) return languageCode;

  return defaultLocale;
}

const DynamicLocaleProvider = ({ 
  children, 
  initialLocale, 
  initialMessages 
}: DynamicLocaleProviderProps) => {
  const language = useGlobalStore(globalGeneralSelectors.language);
  const [currentLocale, setCurrentLocale] = useState<Locale>(initialLocale);
  const [messages, setMessages] = useState(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const appliedExplicitLocale = useRef<Locale | null>(null);
  const loadRequestId = useRef(0);

  const loadLocale = useCallback((targetLocale: Locale) => {
    const requestId = loadRequestId.current + 1;
    loadRequestId.current = requestId;

    setIsLoading(true);

    getMessages(targetLocale)
      .then(msgs => {
        if (requestId !== loadRequestId.current) return;

        setMessages(msgs);
        setCurrentLocale(targetLocale);
      })
      .catch(err => console.error('Failed to load messages:', err))
      .finally(() => {
        if (requestId === loadRequestId.current) {
          setIsLoading(false);
        }
      });
  }, []);

  // Effect 0: refresh bundled initial messages from the runtime manifest.
  useEffect(() => {
    if (language && language !== 'auto') return;

    loadLocale(initialLocale);
  }, [initialLocale, language, loadLocale]);

  // Effect 1: explicit language from store (bridge sets this)
  useEffect(() => {
    if (!language || language === 'auto') return;
    const targetLocale = language as Locale;
    if (!locales.includes(targetLocale)) return;
    if (targetLocale === appliedExplicitLocale.current) return;

    appliedExplicitLocale.current = targetLocale;
    loadLocale(targetLocale);
  }, [language, loadLocale]);

  // Effect 2: auto-detect from browser locale (only when no explicit lang set)
  useEffect(() => {
    if (language && language !== 'auto') return;
    if (appliedExplicitLocale.current) return;

    const targetLocale = resolveBrowserLocale();
    if (targetLocale === currentLocale) return;

    loadLocale(targetLocale);
  }, [language, currentLocale, loadLocale]);

  if (isLoading) {
    // Show a minimal loading state while switching languages
    return (
      <NextIntlClientProvider messages={messages} locale={currentLocale}>
        {children}
      </NextIntlClientProvider>
    );
  }

  return (
    <NextIntlClientProvider messages={messages} locale={currentLocale}>
      {children}
    </NextIntlClientProvider>
  );
};

export default DynamicLocaleProvider;
